import React, { useState } from "react";
import { customerRepo, installmentRepo, ORDER_TYPE } from "../../services";
import { buildInstallmentSchedule } from "../../components/forms/installmentSchedule";
import { useApp } from "../../context/AppContext";
import { useI18n } from "../../i18n";
import PaymentQrModal from "../../components/PaymentQrModal";
import { Button, DataTable, Drawer, Input, Modal, PageHead, ProgressBar, Select, StatCard, StatusBadge, baht, formatDate, useAsync } from "../../components/ui";
import Icon from "../../components/ui/Icon";

export default function Installments() {
  const { orgId, branchId, branches, toast } = useApp();
  const { tx } = useI18n();
  const { data: plans, reload } = useAsync(() => installmentRepo.all(orgId), [orgId], []);
  const [active, setActive] = useState(null);
  const [payPeriod, setPayPeriod] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const { data: customers } = useAsync(() => customerRepo.all(orgId), [orgId], []);

  const openForm = () => setForm({ customerId: "", principal: "", periods: "12", interestRate: "0", startAt: new Date().toISOString().slice(0, 10) });
  const preview = (() => {
    if (!form) return null;
    try {
      return buildInstallmentSchedule(form);
    } catch (e) {
      return null;
    }
  })();

  const savePlan = async () => {
    const customer = (customers || []).find((c) => c.id === form.customerId);
    if (!customer) return toast(tx("เลือกลูกค้า", "Select a customer"), "error");
    setSaving(true);
    try {
      const built = buildInstallmentSchedule(form);
      const max = (plans || []).reduce((m, row) => Math.max(m, Number(String(row.contractNo || "").replace(/\D/g, "")) || 0), 8000);
      await installmentRepo.create(orgId, {
        contractNo: `LN-${max + 1}`,
        customerId: customer.id,
        customerName: customer.name,
        customerPhone: customer.phone,
        principal: Number(form.principal),
        periods: Number(form.periods),
        interestRate: Number(form.interestRate) || 0,
        perPeriod: built.perPeriod,
        startAt: new Date(`${form.startAt}T12:00:00`).toISOString(),
        schedule: built.schedule,
        outstanding: built.outstanding,
        branchId: customer.branchId || branchId || branches?.[0]?.id || "",
        createdAt: new Date().toISOString(),
      });
      setForm(null);
      reload();
      toast(tx("สร้างแผนผ่อนชำระแล้ว", "Installment plan created"), "success");
    } catch (error) {
      toast(error?.message || tx("บันทึกไม่สำเร็จ", "Save failed"), "error");
    } finally {
      setSaving(false);
    }
  };

  const totalOutstanding = (plans || []).reduce((sum, plan) => sum + (Number(plan.outstanding) || 0), 0);
  const overdueCount = (plans || []).reduce((sum, plan) => sum + (plan.schedule || []).filter((s) => s.status === "overdue").length, 0);

  const markPaid = async () => {
    if (!active || !payPeriod) return;
    const schedule = active.schedule.map((row) => (row.period === payPeriod.period ? { ...row, status: "paid", paidAt: new Date().toISOString() } : row));
    const outstanding = schedule.filter((s) => s.status !== "paid").reduce((sum, s) => sum + s.amount, 0);
    await installmentRepo.update(orgId, active.id, { schedule, outstanding });
    setActive((prev) => ({ ...prev, schedule, outstanding }));
    setPayPeriod(null);
    reload();
    toast(tx("บันทึกการชำระงวดเรียบร้อย", "Installment paid"), "success");
  };

  return (
    <>
      <PageHead
        title={tx("ผ่อนชำระ & สินเชื่อ", "Installments & loans")}
        subtitle={tx("ติดตามตารางผ่อนชำระ ดอกเบี้ย และหนี้คงค้าง", "Track schedules, interest and outstanding balance")}
        actions={<Button variant="primary" onClick={openForm}>+ {tx("สร้างแผนผ่อน", "New plan")}</Button>}
      />

      <div className="stat-grid">
        <StatCard icon={<Icon name="chartLine" />} label={tx("ยอดคงค้างรวม", "Total outstanding")} value={baht(totalOutstanding)} />
        <StatCard icon={<Icon name="warning" />} label={tx("งวดเกินกำหนด", "Overdue periods")} value={overdueCount} tone={overdueCount ? "var(--c-rose)" : undefined} />
        <StatCard icon={<Icon name="receipt" />} label={tx("สัญญาทั้งหมด", "Total contracts")} value={(plans || []).length} />
      </div>

      <DataTable
        rows={plans || []}
        onRowClick={setActive}
        emptyText={tx("ยังไม่มีสัญญาผ่อนชำระ", "No installment plans")}
        columns={[
          { key: "contractNo", label: tx("เลขที่สัญญา", "Contract no.") },
          { key: "customerName", label: tx("ลูกค้า", "Customer") },
          { key: "principal", label: tx("ยอดเงินต้น", "Principal"), align: "right", render: (row) => baht(row.principal, 0) },
          { key: "periods", label: tx("งวด", "Periods"), align: "right" },
          { key: "outstanding", label: tx("ค้างชำระ", "Outstanding"), align: "right", render: (row) => <span className={row.outstanding > 0 ? "u-danger u-strong" : ""}>{baht(row.outstanding)}</span> },
        ]}
      />

      <Drawer open={!!active} title={active?.contractNo} onClose={() => setActive(null)}>
        {active ? (
          <>
            <div className="u-mb-16">
              <div className="u-strong u-fs-18">{active.customerName}</div>
              <div className="u-fs-13 u-muted">{tx("เงินต้น", "Principal")} {baht(active.principal, 0)} · {tx("ดอกเบี้ย", "Interest")} {active.interestRate}%</div>
              <ProgressBar percent={((active.periods - active.schedule.filter((s) => s.status !== "paid").length) / active.periods) * 100} />
            </div>
            {active.schedule.map((row) => (
              <div key={row.period} className="u-between" style={{ padding: "9px 0", borderBottom: "1px solid var(--c-slate-100)" }}>
                <div>
                  <div className="u-strong">{tx("งวดที่", "Period")} {row.period}</div>
                  <div className="u-fs-12 u-muted">{formatDate(row.dueAt)}</div>
                </div>
                <div className="u-right">
                  <div className="u-strong">{baht(row.amount)}</div>
                  <StatusBadge status={row.status} />
                </div>
                {row.status !== "paid" ? (
                  <Button size="sm" variant="primary" onClick={() => setPayPeriod(row)}>{tx("รับชำระ", "Collect")}</Button>
                ) : null}
              </div>
            ))}
          </>
        ) : null}
      </Drawer>

      <PaymentQrModal
        open={!!payPeriod}
        title={tx(`ชำระงวดที่ ${payPeriod?.period}`, `Pay period ${payPeriod?.period}`)}
        subtitle={active?.customerName}
        amount={payPeriod?.amount || 0}
        orderType={ORDER_TYPE.installment}
        reference={active?.contractNo}
        items={[{ productName: `${active?.contractNo} - ${tx("งวดที่", "Period")} ${payPeriod?.period}`, quantity: 1, price: payPeriod?.amount || 0 }]}
        onClose={() => setPayPeriod(null)}
        onPaid={markPaid}
      />

      <Modal
        open={!!form}
        title={tx("สร้างแผนผ่อนชำระ", "New installment plan")}
        onClose={() => setForm(null)}
        footer={<><Button onClick={() => setForm(null)}>{tx("ยกเลิก", "Cancel")}</Button><Button variant="primary" disabled={saving || !preview} onClick={savePlan}>{tx("สร้างแผน", "Create plan")}</Button></>}
      >
        {form ? (
          <>
            <Select
              label={tx("ลูกค้า", "Customer")}
              placeholder={tx("เลือกลูกค้า", "Select customer")}
              options={(customers || []).map((c) => ({ value: c.id, label: `${c.name} · ${c.phone || c.code || ""}` }))}
              value={form.customerId}
              onChange={(e) => setForm({ ...form, customerId: e.target.value })}
            />
            <Input label={tx("ยอดเงินต้น (บาท)", "Principal (THB)")} type="number" min="1" value={form.principal} onChange={(e) => setForm({ ...form, principal: e.target.value })} />
            <Input label={tx("จำนวนงวด (เดือน)", "Periods (months)")} type="number" min="1" max="120" value={form.periods} onChange={(e) => setForm({ ...form, periods: e.target.value })} />
            <Input label={tx("ดอกเบี้ย (% ต่อเดือน แบบคงที่)", "Interest (% per month, flat)")} type="number" min="0" step="0.01" value={form.interestRate} onChange={(e) => setForm({ ...form, interestRate: e.target.value })} />
            <Input label={tx("วันเริ่มผ่อน (งวดแรกครบกำหนดอีก 1 เดือน)", "Start date (first due in 1 month)")} type="date" value={form.startAt} onChange={(e) => setForm({ ...form, startAt: e.target.value })} />
            {preview ? (
              <div className="u-fs-13 u-mt-8">
                {tx("ยอดรวมทั้งสิ้น", "Total")} <span className="u-strong">{baht(preview.total)}</span> · {tx("ต่องวด", "per period")} <span className="u-strong">{baht(preview.perPeriod)}</span>
              </div>
            ) : null}
          </>
        ) : null}
      </Modal>
    </>
  );
}
